import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { format, parseISO } from 'date-fns';
import { es, enUS, de as deLocale } from 'date-fns/locale';
import {
  LayoutDashboard,
  Download,
  Loader2,
  Activity,
  TrendingUp,
  AlertTriangle,
  Users,
  Euro,
  CreditCard,
  Clock,
  CheckCircle2,
  XCircle,
  Crown,
  AlertCircle,
  Zap,
  TrendingDown,
  Building2,
  MessageCircle,
  Calendar,
  ShieldAlert,
} from 'lucide-react';
import { toast } from 'sonner';
import { Progress } from '../components/ui/progress';
import { Button } from '../components/ui/button';

const InfoTooltip = ({ text }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <div style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
      <button
        aria-label="Información"
        onClick={() => setOpen(v => !v)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px',
          color: 'rgba(255,255,255,0.3)', fontSize: '13px', lineHeight: 1,
          transition: 'color 0.15s',
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        ℹ
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 100,
          background: '#18181b', border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: '8px', padding: '8px 12px', width: '220px',
          fontSize: '12px', color: 'rgba(255,255,255,0.65)', lineHeight: 1.55,
          boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
          pointerEvents: 'none',
        }}>
          {text}
        </div>
      )}
    </div>
  );
};

const daysSince = (isoDate) => {
  if (!isoDate) return null;
  return Math.floor((Date.now() - new Date(isoDate).getTime()) / (1000 * 60 * 60 * 24));
};

const SaasDashboard = () => {
  const { api } = useAuth();
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/dashboard/saas-stats');
      setStats(res.data);
    } catch {
      toast.error(t('success.dashboard.saasStatsError'));
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
      </div>
    );
  }
  if (!stats) return null;

  const centersDelta = stats.newCentersThisMonth - stats.newCentersLastMonth;

  return (
    <div className="p-4 md:p-6 space-y-8 h-full overflow-y-auto" data-testid="dashboard-page">

      {/* Header */}
      <div className="flex items-center gap-3">
        <LayoutDashboard className="w-6 h-6 text-zinc-400" />
        <h1 className="font-display text-2xl font-bold tracking-tight uppercase">
          Dashboard SaaS
        </h1>
      </div>

      {/* ── Sección 1: Plataforma ── */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Building2 className="w-4 h-4 text-zinc-400" />
          <h2 className="text-sm font-semibold text-zinc-400 uppercase tracking-widest">
            Plataforma
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">

          {/* Centros activos */}
          <div className="bg-card border border-zinc-800 rounded-sm p-4">
            <div className="flex items-center gap-2 mb-3">
              <Building2 className="w-4 h-4 text-zinc-400" />
              <p className="text-xs text-zinc-500 uppercase tracking-wider">Centros activos</p>
              <InfoTooltip text="Número total de centros con estado activo en la plataforma" />
            </div>
            <div className="text-3xl font-bold tabular-nums">{stats.totalActiveCenters}</div>
          </div>

          {/* Nuevos este mes */}
          <div className="bg-card border border-zinc-800 rounded-sm p-4">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-4 h-4 text-zinc-400" />
              <p className="text-xs text-zinc-500 uppercase tracking-wider">Nuevos este mes</p>
              <InfoTooltip text="Centros creados este mes comparado con el mes anterior" />
            </div>
            <div className="text-3xl font-bold tabular-nums">{stats.newCentersThisMonth}</div>
            <div className="flex items-center gap-1 mt-1 text-xs">
              {centersDelta > 0 ? (
                <TrendingUp className="w-3 h-3 text-green-400" />
              ) : centersDelta < 0 ? (
                <TrendingDown className="w-3 h-3 text-red-400" />
              ) : null}
              <span className={
                centersDelta > 0 ? 'text-green-400' :
                centersDelta < 0 ? 'text-red-400' :
                'text-zinc-500'
              }>
                {centersDelta > 0 ? `+${centersDelta}` : centersDelta}{' '}
                vs mes anterior ({stats.newCentersLastMonth})
              </span>
            </div>
          </div>

          {/* Centros con WhatsApp */}
          <div className="bg-card border border-zinc-800 rounded-sm p-4">
            <div className="flex items-center gap-2 mb-3">
              <MessageCircle className="w-4 h-4 text-green-400" />
              <p className="text-xs text-zinc-500 uppercase tracking-wider">Con WhatsApp</p>
              <InfoTooltip text="Centros que tienen configurado el token de WhatsApp Business" />
            </div>
            <div className="text-3xl font-bold tabular-nums">{stats.centersWithWhatsapp}</div>
            {stats.totalActiveCenters > 0 && (
              <div className="text-xs text-zinc-500 mt-1">
                {Math.round((stats.centersWithWhatsapp / stats.totalActiveCenters) * 100)}% del total
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── Sección 2: Actividad ── */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Activity className="w-4 h-4 text-zinc-400" />
          <h2 className="text-sm font-semibold text-zinc-400 uppercase tracking-widest">
            Actividad
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

          {/* Total clientes */}
          <div className="bg-card border border-zinc-800 rounded-sm p-4">
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-4 h-4 text-zinc-400" />
              <p className="text-xs text-zinc-500 uppercase tracking-wider">Total clientes</p>
              <InfoTooltip text="Clientes activos en toda la plataforma, en todos los centros" />
            </div>
            <div className="text-3xl font-bold tabular-nums">{stats.totalClients}</div>
          </div>

          {/* Reservas este mes */}
          <div className="bg-card border border-zinc-800 rounded-sm p-4">
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="w-4 h-4 text-zinc-400" />
              <p className="text-xs text-zinc-500 uppercase tracking-wider">Reservas este mes</p>
              <InfoTooltip text="Total de reservas creadas en toda la plataforma durante el mes actual" />
            </div>
            <div className="text-3xl font-bold tabular-nums">{stats.bookingsThisMonth}</div>
          </div>
        </div>
      </section>

      {/* ── Sección 3: Riesgo churn ── */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <ShieldAlert className="w-4 h-4 text-zinc-400" />
          <h2 className="text-sm font-semibold text-zinc-400 uppercase tracking-widest">
            Riesgo de churn
          </h2>
        </div>

        <div className="bg-card border border-zinc-800 rounded-sm p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-orange-400" />
            <p className="text-xs text-zinc-500 uppercase tracking-wider">
              Sin reservas en 30 días
            </p>
            {stats.churnRiskCenters.length > 0 && (
              <span className="ml-auto text-xs bg-orange-400/10 text-orange-400 px-2 py-0.5 rounded">
                {stats.churnRiskCenters.length}
              </span>
            )}
            <InfoTooltip text="Centros activos que no han tenido ninguna reserva en los últimos 30 días" />
          </div>

          {stats.churnRiskCenters.length === 0 ? (
            <div className="flex items-center gap-2 text-sm text-green-400">
              <CheckCircle2 className="w-4 h-4" />
              {t('dashboard.saas.allCentersActive')}
            </div>
          ) : (
            <div className="space-y-2">
              {stats.churnRiskCenters.map((c) => {
                const days = daysSince(c.lastBookingDate);
                return (
                  <div key={c.centerId} className="flex items-center justify-between text-sm">
                    <span className="text-zinc-200 truncate mr-4">{c.centerName}</span>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {c.lastBookingDate ? (
                        <>
                          <span className="text-zinc-500 text-xs">
                            {t('dashboard.saas.lastBooking')} {format(new Date(c.lastBookingDate), 'dd/MM/yyyy')}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded tabular-nums ${
                            days > 60 ? 'bg-red-400/10 text-red-400' : 'bg-orange-400/10 text-orange-400'
                          }`}>
                            {t('dashboard.saas.daysAgo', { n: days })}
                          </span>
                        </>
                      ) : (
                        <span className="text-xs bg-red-400/10 text-red-400 px-2 py-0.5 rounded">
                          {t('dashboard.saas.noBookings')}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export const Dashboard = () => {
  const { api, language, user } = useAuth();
  const { t } = useTranslation();
  const locale = language === 'es' ? es : language === 'de' ? deLocale : enUS;

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('centro');
  const [aiStats, setAiStats] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [occupancyWeek, setOccupancyWeek] = useState([]);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/dashboard/stats');
      setStats(res.data);
    } catch {
      toast.error(t('dashboard.loadError'));
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  const fetchAiStats = useCallback(async () => {
    try {
      setAiLoading(true);
      const res = await api.get('/dashboard/ai-secretary-summary');
      setAiStats(res.data);
    } catch {
      toast.error(t('success.dashboard.aiSecretaryStatsError'));
    } finally {
      setAiLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  const fetchOccupancyWeek = useCallback(async () => {
    try {
      const res = await api.get('/dashboard/occupancy-week');
      setOccupancyWeek(res.data);
    } catch { /* non-critical */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  useEffect(() => {
    if (user?.role !== 'SUPER_ADMIN') fetchStats();
  }, [fetchStats, user?.role]);

  useEffect(() => {
    if (user?.role !== 'SUPER_ADMIN') fetchOccupancyWeek();
  }, [fetchOccupancyWeek, user?.role]);

  useEffect(() => {
    if (user?.role !== 'SUPER_ADMIN' && activeTab === 'secretaria') fetchAiStats();
  }, [fetchAiStats, activeTab, user?.role]);

  if (user?.role === 'SUPER_ADMIN') return <SaasDashboard />;

  const formatCurrency = (val) =>
    new Intl.NumberFormat(language === 'es' ? 'es-ES' : language === 'de' ? 'de-DE' : 'en-US', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(val ?? 0);

  const packsDelta = stats ? stats.packsThisMonth - stats.packsLastMonth : 0;

  const handleExportKpis = async () => {
    try {
      const res = await api.get('/dashboard/stats/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `adamsched-kpis-${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error(t('success.dashboard.exportKpisError'));
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-6 h-full overflow-y-auto" data-testid="dashboard-page">

      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <LayoutDashboard className="w-6 h-6 text-zinc-400" />
          <h1 className="font-display text-2xl font-bold tracking-tight uppercase">
            {t('dashboard.title')}
          </h1>
        </div>
        <Button variant="outline" size="sm" onClick={handleExportKpis} data-testid="export-kpis-btn">
          <Download className="w-4 h-4 mr-2" />
          Exportar Excel
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-zinc-800">
        {[
          { id: 'centro', label: 'Centro' },
          { id: 'secretaria', label: 'Secretaria IA' },
        ].map(({ id, label }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === id
                ? 'text-white border-b-2 border-white -mb-px'
                : 'text-zinc-500 hover:text-zinc-300'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── Tab: Centro — loading ── */}
      {activeTab === 'centro' && loading && (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
        </div>
      )}

      {activeTab === 'centro' && !loading && stats && <div className="space-y-8">

      {/* ── Sección 1: Ocupación y asistencia ── */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Activity className="w-4 h-4 text-zinc-400" />
          <h2 className="text-sm font-semibold text-zinc-400 uppercase tracking-widest">
            {t('dashboard.sections.occupancy')}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

          {/* Ocupación por servicio esta semana */}
          <div className="md:col-span-2 bg-card border border-zinc-800 rounded-sm p-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs text-zinc-500 uppercase tracking-wider">
                {t('dashboard.occupancyWeek')}
              </p>
              <InfoTooltip text="Reservas activas esta semana por servicio (barra relativa al más ocupado)" />
            </div>
            {occupancyWeek.length === 0 ? (
              <p className="text-sm text-zinc-600">{t('dashboard.noData')}</p>
            ) : (
              (() => {
                const maxSlots = Math.max(...occupancyWeek.map(s => s.usedSlots), 1);
                return occupancyWeek.map((svc) => (
                  <div key={svc.calendarId} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-zinc-200">{svc.serviceName}</span>
                      <span className="text-zinc-400 tabular-nums">
                        {svc.usedSlots} reservas
                      </span>
                    </div>
                    <Progress
                      value={Math.round((svc.usedSlots / maxSlots) * 100)}
                      className="h-1.5 bg-zinc-800"
                    />
                  </div>
                ));
              })()
            )}
          </div>

          {/* Asistencia este mes */}
          <div className="bg-card border border-zinc-800 rounded-sm p-4 flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <p className="text-xs text-zinc-500 uppercase tracking-wider">
                {t('dashboard.attendanceMonth')}
              </p>
              <InfoTooltip text="Sesiones marcadas como asistidas y no presentados en el mes actual" />
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
              <div>
                <div className="text-2xl font-bold tabular-nums">{stats.attendanceThisMonth.attended}</div>
                <div className="text-xs text-zinc-500">{t('dashboard.attended')}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <XCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
              <div>
                <div className="text-2xl font-bold tabular-nums">{stats.attendanceThisMonth.noShow}</div>
                <div className="text-xs text-zinc-500">{t('dashboard.noShow')}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Top 5 clientes */}
        {stats.topClients.length > 0 && (
          <div className="mt-4 bg-card border border-zinc-800 rounded-sm p-4">
            <div className="flex items-center gap-2 mb-3">
              <Crown className="w-4 h-4 text-yellow-400" />
              <p className="text-xs text-zinc-500 uppercase tracking-wider">
                {t('dashboard.topClients')}
              </p>
              <InfoTooltip text="Clientes con más sesiones asistidas este mes" />
            </div>
            <div className="space-y-2">
              {stats.topClients.map((c, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-zinc-600 w-4 tabular-nums">{i + 1}.</span>
                    <span className="text-zinc-200">{c.clientName}</span>
                  </div>
                  <span className="text-zinc-400 tabular-nums">
                    {c.sessions} {t('dashboard.sessions')}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* ── Sección 2: Económico ── */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-4 h-4 text-zinc-400" />
          <h2 className="text-sm font-semibold text-zinc-400 uppercase tracking-widest">
            {t('dashboard.sections.financial')}
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">

          {/* Ingresos del mes */}
          <div className="bg-card border border-zinc-800 rounded-sm p-4">
            <div className="flex items-center gap-2 mb-3">
              <Euro className="w-4 h-4 text-zinc-400" />
              <p className="text-xs text-zinc-500 uppercase tracking-wider">
                {t('dashboard.revenueMonth')}
              </p>
              <InfoTooltip text="Suma del precio de los bonos activos creados este mes" />
            </div>
            <div className="text-3xl font-bold tabular-nums">
              {formatCurrency(stats.revenueThisMonth)}
            </div>
          </div>

          {/* Bonos vendidos */}
          <div className="bg-card border border-zinc-800 rounded-sm p-4">
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-4 h-4 text-zinc-400" />
              <p className="text-xs text-zinc-500 uppercase tracking-wider">
                {t('dashboard.packsSold')}
              </p>
              <InfoTooltip text="Bonos creados este mes comparado con el mes anterior" />
            </div>
            <div className="text-3xl font-bold tabular-nums">{stats.packsThisMonth}</div>
            <div className="flex items-center gap-1 mt-1 text-xs">
              {packsDelta > 0 ? (
                <TrendingUp className="w-3 h-3 text-green-400" />
              ) : packsDelta < 0 ? (
                <TrendingDown className="w-3 h-3 text-red-400" />
              ) : null}
              <span className={
                packsDelta > 0 ? 'text-green-400' :
                packsDelta < 0 ? 'text-red-400' :
                'text-zinc-500'
              }>
                {packsDelta > 0 ? `+${packsDelta}` : packsDelta}{' '}
                {t('dashboard.vsLastMonth')} ({stats.packsLastMonth})
              </span>
            </div>
          </div>

          {/* Pendiente de cobro */}
          <div className="bg-card border border-zinc-800 rounded-sm p-4">
            <div className="flex items-center gap-2 mb-3">
              <CreditCard className="w-4 h-4 text-zinc-400" />
              <p className="text-xs text-zinc-500 uppercase tracking-wider">
                {t('dashboard.pendingPayment')}
              </p>
              <InfoTooltip text="Bonos en estado pendiente de pago y su importe total" />
            </div>
            <div className="text-3xl font-bold tabular-nums">
              {stats.pendingPayment.count}
            </div>
            <div className="text-xs text-zinc-500 mt-1">
              {formatCurrency(stats.pendingPayment.total)} {t('dashboard.outstanding')}
            </div>
          </div>
        </div>
      </section>

      {/* ── Sección 3: Alertas ── */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <AlertTriangle className="w-4 h-4 text-zinc-400" />
          <h2 className="text-sm font-semibold text-zinc-400 uppercase tracking-widest">
            {t('dashboard.sections.alerts')}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

          {/* Bonos que caducan en 30 días */}
          <div className="bg-card border border-zinc-800 rounded-sm p-4">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-4 h-4 text-yellow-400" />
              <p className="text-xs text-zinc-500 uppercase tracking-wider">
                {t('dashboard.expiringSoon')}
              </p>
              {stats.expiringSoon.length > 0 && (
                <span className="ml-auto text-xs bg-yellow-400/10 text-yellow-400 px-2 py-0.5 rounded">
                  {stats.expiringSoon.length}
                </span>
              )}
              <InfoTooltip text="Bonos activos cuya fecha de caducidad es en los próximos 30 días" />
            </div>
            {stats.expiringSoon.length === 0 ? (
              <p className="text-xs text-zinc-600">{t('dashboard.allGood')}</p>
            ) : (
              <div className="space-y-2">
                {stats.expiringSoon.map((item, i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <span className="text-zinc-300 truncate mr-2">{item.clientName}</span>
                    <span className="text-zinc-500 flex-shrink-0">
                      {format(parseISO(item.expiresAt), 'dd/MM', { locale })}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Clientes con deuda */}
          <div className="bg-card border border-zinc-800 rounded-sm p-4">
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle className="w-4 h-4 text-red-400" />
              <p className="text-xs text-zinc-500 uppercase tracking-wider">
                {t('dashboard.debtClients')}
              </p>
              {stats.debtClients.length > 0 && (
                <span className="ml-auto text-xs bg-red-400/10 text-red-400 px-2 py-0.5 rounded">
                  {stats.debtClients.length}
                </span>
              )}
              <InfoTooltip text="Clientes con sesiones consumidas sin bono activo" />
            </div>
            {stats.debtClients.length === 0 ? (
              <p className="text-xs text-zinc-600">{t('dashboard.allGood')}</p>
            ) : (
              <div className="space-y-2">
                {stats.debtClients.map((item, i) => (
                  <div key={i} className="flex items-center justify-between text-xs gap-2">
                    <span className="text-zinc-300 truncate">{item.clientName}</span>
                    <div className="flex items-center gap-2 flex-shrink-0 tabular-nums">
                      {item.debtSessions > 0 && (
                        <span className="text-amber-400">{item.debtSessions} ses.</span>
                      )}
                      {item.pendingAmount > 0 && (
                        <span className="text-red-400">{item.pendingAmount.toFixed(0)}€</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Bonos casi agotados */}
          <div className="bg-card border border-zinc-800 rounded-sm p-4">
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-4 h-4 text-orange-400" />
              <p className="text-xs text-zinc-500 uppercase tracking-wider">
                {t('dashboard.lowSessions')}
              </p>
              {stats.lowSessions.length > 0 && (
                <span className="ml-auto text-xs bg-orange-400/10 text-orange-400 px-2 py-0.5 rounded">
                  {stats.lowSessions.length}
                </span>
              )}
              <InfoTooltip text="Bonos activos con 2 o menos sesiones restantes" />
            </div>
            {stats.lowSessions.length === 0 ? (
              <p className="text-xs text-zinc-600">{t('dashboard.allGood')}</p>
            ) : (
              <div className="space-y-2">
                {stats.lowSessions.map((item, i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <span className="text-zinc-300 truncate mr-2">{item.clientName}</span>
                    <span className="text-orange-400 flex-shrink-0 tabular-nums">
                      {item.remainingSessions} {t('dashboard.sessionsLeft')}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </section>

      </div>}

      {/* ── Tab: Secretaria IA ── */}
      {activeTab === 'secretaria' && (
        <div className="space-y-6">
          {aiLoading && (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
            </div>
          )}

          {!aiLoading && aiStats && !aiStats.enabled && (
            <div className="bg-card border border-zinc-800 rounded-sm p-8 text-center">
              <Activity className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-400 text-sm">Secretaria IA no activada en este centro</p>
            </div>
          )}

          {!aiLoading && aiStats && aiStats.enabled && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="bg-card border border-zinc-800 rounded-sm p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Activity className="w-4 h-4 text-zinc-400" />
                    <p className="text-xs text-zinc-500 uppercase tracking-wider">Llamadas</p>
                    <InfoTooltip text="Llamadas completadas por la Secretaria IA en los últimos 30 días" />
                  </div>
                  <div className="text-3xl font-bold tabular-nums">{aiStats.totals.llamadas}</div>
                  <div className="text-xs text-zinc-500 mt-1">últimos 30 días</div>
                </div>
                <div className="bg-card border border-zinc-800 rounded-sm p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-4 h-4 text-zinc-400" />
                    <p className="text-xs text-zinc-500 uppercase tracking-wider">Minutos</p>
                    <InfoTooltip text="Minutos facturados por la Secretaria IA en los últimos 30 días" />
                  </div>
                  <div className="text-3xl font-bold tabular-nums">{aiStats.totals.minutos}</div>
                  <div className="text-xs text-zinc-500 mt-1">últimos 30 días</div>
                </div>
                <div className="bg-card border border-zinc-800 rounded-sm p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="w-4 h-4 text-zinc-400" />
                    <p className="text-xs text-zinc-500 uppercase tracking-wider">Citas creadas</p>
                    <InfoTooltip text="Reservas creadas en los últimos 30 días (aproximación — incluye todas las fuentes)" />
                  </div>
                  <div className="text-3xl font-bold tabular-nums">{aiStats.totals.citasCreadas}</div>
                  <div className="text-xs text-zinc-500 mt-1">últimos 30 días</div>
                </div>
                <div className="bg-card border border-zinc-800 rounded-sm p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <XCircle className="w-4 h-4 text-red-400" />
                    <p className="text-xs text-zinc-500 uppercase tracking-wider">Cancelaciones</p>
                    <InfoTooltip text="Reservas canceladas en los últimos 30 días (aproximación — incluye todas las fuentes)" />
                  </div>
                  <div className="text-3xl font-bold tabular-nums">{aiStats.totals.canceladas}</div>
                  <div className="text-xs text-zinc-500 mt-1">últimos 30 días</div>
                </div>
              </div>

              <div className="bg-card border border-zinc-800 rounded-sm p-4">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp className="w-4 h-4 text-zinc-400" />
                  <h2 className="text-sm font-semibold text-zinc-400 uppercase tracking-widest">
                    Actividad diaria
                  </h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-zinc-500 text-xs uppercase tracking-wider border-b border-zinc-800">
                        <th className="text-left py-2 pr-4">Fecha</th>
                        <th className="text-right py-2 px-4">Llamadas</th>
                        <th className="text-right py-2 px-4">Min.</th>
                        <th className="text-right py-2 px-4">Citas</th>
                        <th className="text-right py-2 pl-4">Cancel.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...aiStats.daily].reverse().map((row) => (
                        <tr key={row.date} className="border-b border-zinc-800/50 hover:bg-zinc-800/20">
                          <td className="py-1.5 pr-4 text-zinc-400 tabular-nums">
                            {format(parseISO(row.date), 'dd MMM', { locale })}
                          </td>
                          <td className="py-1.5 px-4 text-right tabular-nums">
                            {row.llamadas > 0 ? row.llamadas : <span className="text-zinc-700">—</span>}
                          </td>
                          <td className="py-1.5 px-4 text-right tabular-nums">
                            {row.minutos > 0 ? row.minutos : <span className="text-zinc-700">—</span>}
                          </td>
                          <td className="py-1.5 px-4 text-right tabular-nums">
                            {row.citasCreadas > 0 ? row.citasCreadas : <span className="text-zinc-700">—</span>}
                          </td>
                          <td className="py-1.5 pl-4 text-right tabular-nums">
                            {row.canceladas > 0
                              ? <span className="text-red-400">{row.canceladas}</span>
                              : <span className="text-zinc-700">—</span>
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
